import { getConfig, getCurrencyFormatter, imagePathResolver, NumberFormats as nf, ROUTES } from '@shared/utils';
import { getTranslations } from 'next-intl/server';
import Image from 'next/image';
import { BackChevron } from '../../BackChevron/BackChevron';
import { ContentDetailsGroup } from '../../ContentDetailsGroup/ContentDetailsGroup';
import { RichContent } from '../../RichContent/RichContent';
import { SecondaryHeaderClient } from '../../SecondaryHeaderContainer/SecondaryHeaderContainer';
import type { ScratchcardDetails, ScratchcardPayTable } from '../types';
import { getScratchcardMaxPrize, getScratchcardUnitPrice } from '../utils/scratchcardProductValues';
import { ScratchcardPrizeTable } from './components/ScratchcardPrizeTable/ScratchcardPrizeTable';
import { ScratchcardPurchasePanel } from './components/ScratchcardPurchasePanel/ScratchcardPurchasePanel';

type ScratchcardProductPageProps = {
  scratchcard: ScratchcardDetails;
  payTables: ScratchcardPayTable[];
};

async function ScratchcardProductPage({ scratchcard, payTables }: ScratchcardProductPageProps) {
  const t = await getTranslations('scratchcards.product_page');
  const currencyFormatter = await getCurrencyFormatter();
  const { baseImgUrl } = getConfig();
  const imagePath = scratchcard.landingImageUrl || scratchcard.imageUrl;
  const imageSrc = imagePath ? imagePathResolver(imagePath, baseImgUrl) : '';
  const unitPrice = getScratchcardUnitPrice(scratchcard.bundles);
  const maxPrize = getScratchcardMaxPrize(payTables);
  const formattedMaxPrize = currencyFormatter(maxPrize).format(nf.MIXED_DECIMALS).get();
  const formattedCardPrice = currencyFormatter(unitPrice).format(nf.MIXED_DECIMALS).get();
  const prizeTableRows = [...payTables]
    .sort((left, right) => left.tier - right.tier)
    .map(row => ({
      tier: row.tier,
      prize: t('prize_count', { count: row.prizesAvailable }),
      amount: currencyFormatter(row.amount).format(nf.WITH_DECIMALS).get(),
    }));

  const pods = [
    scratchcard.odds && { label: t('odds'), info: t('odds_value', { odds: scratchcard.odds }) },
    unitPrice > 0 && { label: t('price'), info: formattedCardPrice },
    maxPrize > 0 && { label: t('max_prize'), info: formattedMaxPrize },
  ].filter((pod): pod is { label: string; info: string } => Boolean(pod));

  return (
    <div className="pb-24" data-testid="scratchcard-product-page">
      <SecondaryHeaderClient className="ll-container-full py-3">
        <div className="flex items-center gap-2 ll-container-inner">
          <BackChevron fallbackUrl={ROUTES.SCRATCHCARDS.DISCOVERY} headerVariant="subheader" />
          <h5 data-testid="scratchcard-product-title">{scratchcard.localisedName}</h5>
        </div>
      </SecondaryHeaderClient>

      <div className="ll-grid-container">
        <div className="ll-container-inner flex flex-col gap-6 pt-6 md:grid md:grid-cols-2 md:items-start">
          <div className="order-1 flex flex-col gap-2">
            {imageSrc ? (
              <div className="relative aspect-video w-full overflow-hidden rounded-md">
                <Image
                  src={imageSrc}
                  priority
                  alt={scratchcard.localisedName}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  data-testid="scratchcard-product-image"
                />
              </div>
            ) : null}
            {pods.length > 0 ? <ContentDetailsGroup className="[&_p:last-child]:normal-case" columns={3} pods={pods} data-testid="scratchcard-product-details" /> : null}
          </div>

          <div className="order-3 md:order-2">
            <ScratchcardPurchasePanel unitPrice={unitPrice} title={scratchcard.title} localisedName={scratchcard.localisedName} />
          </div>

          {scratchcard.description ? (
            <section className="order-2 md:order-3 md:col-span-2 flex flex-col gap-4" data-testid="scratchcard-info">
              <p className="paragraph-strong text-foreground">{t('info_title')}</p>
              <RichContent information={scratchcard.description} className="paragraph text-foreground-subtle" />
            </section>
          ) : null}

          <div className="order-4 md:col-span-2">
            <ScratchcardPrizeTable rows={prizeTableRows} title={t('prizetable')} tierLabel={t('tier')} prizeLabel={t('prize')} amountLabel={t('amount')} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { ScratchcardProductPage };
